import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  IconHighlight,
  Icon,
  Button,
  Tag,
  Typography,
} from '@ann/web-components';
import { NOTE_ICON_CONFIG, NOTES_CARD_MAX_CHARACTERS, NOTES_METADATA_ICONS } from '../../constants';
import {
  ItemContainer,
  Row,
  LeftSection,
  RightSection,
  TitleWrapper,
  DescriptionRow,
  DescriptionWrapper,
  MetadataBar,
  MetadataItem,
} from './NotesListItem.styled';

/**
 * Single note list item. Uses @ann/web-components for icon, tag, typography, button.
 * Expanded: full content. Compressed: icon, title (truncated), chevron only.
 * Shows metadata bar on hover when expanded and metadata exists (CSS-only).
 */
const NotesListItem = ({ item, isCompressed, onItemClick }) => {
  const hasMetadata = useMemo(() => {
    const m = item.metadata;
    return (
      item.author ||
      (m?.commentsCount > 0) ||
      (m?.attachmentsCount > 0) ||
      (m?.tasksCount > 0)
    );
  }, [item.author, item.metadata]);

  const handleClick = useCallback(() => {
    onItemClick?.(item);
  }, [item, onItemClick]);

  const dueTagText = item.dueDate ? `Due ${item.dueDate}` : '';

  return (
    <ItemContainer
      data-compressed={isCompressed || undefined}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <Row>
        <LeftSection>
          <IconHighlight
            icon={NOTE_ICON_CONFIG.icon}
            bgColor={NOTE_ICON_CONFIG.bgColor}
            iconColor={NOTE_ICON_CONFIG.iconColor}
            size="small"
          />
          <TitleWrapper>
            <Typography
              category="body"
              size="medium"
              weight="medium"
              color="title"
              maxCharacters={NOTES_CARD_MAX_CHARACTERS}
            >
              {item.title}
            </Typography>
          </TitleWrapper>
        </LeftSection>
        <RightSection>
          {!isCompressed && dueTagText && (
            <Tag
              icon="calendarToday"
              text={dueTagText}
              size="small"
              color="informative"
            />
          )}
          {!isCompressed && item.timeAgo && (
            <Typography category="body" size="small" weight="medium" color="caption">
              {item.timeAgo}
            </Typography>
          )}
          <Button
            type="tertiary"
            size="small"
            iconLeft="chevronRight"
            aria-label="Ver detalles"
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
          />
        </RightSection>
      </Row>

      {!isCompressed && item.description && (
        <DescriptionRow>
          <DescriptionWrapper>
            <Typography
              category="body"
              size="small"
              weight="regular"
              color="body"
              maxCharacters={NOTES_CARD_MAX_CHARACTERS}
            >
              {item.description}
            </Typography>
          </DescriptionWrapper>
        </DescriptionRow>
      )}

      {!isCompressed && hasMetadata && (
        <MetadataBar>
          {item.author && (
            <MetadataItem>
              <Typography category="body" size="small" weight="medium" color="body">
                {item.author}
              </Typography>
            </MetadataItem>
          )}
          {item.metadata?.commentsCount > 0 && (
            <MetadataItem>
              <Icon materialName={NOTES_METADATA_ICONS.comments} size="small" />
              <Typography category="body" size="small" weight="medium" color="body">
                {item.metadata.commentsCount}
              </Typography>
            </MetadataItem>
          )}
          {item.metadata?.attachmentsCount > 0 && (
            <MetadataItem>
              <Icon materialName={NOTES_METADATA_ICONS.attachments} size="small" />
              <Typography category="body" size="small" weight="medium" color="body">
                {item.metadata.attachmentsCount}
              </Typography>
            </MetadataItem>
          )}
          {item.metadata?.tasksCount > 0 && (
            <MetadataItem>
              <Icon materialName={NOTES_METADATA_ICONS.tasks} size="small" />
              <Typography category="body" size="small" weight="medium" color="body">
                {item.metadata.tasksCount}
              </Typography>
            </MetadataItem>
          )}
        </MetadataBar>
      )}
    </ItemContainer>
  );
};

NotesListItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    description: PropTypes.string,
    dueDate: PropTypes.string,
    author: PropTypes.string,
    timeAgo: PropTypes.string,
    metadata: PropTypes.shape({
      commentsCount: PropTypes.number,
      attachmentsCount: PropTypes.number,
      tasksCount: PropTypes.number,
    }),
  }).isRequired,
  isCompressed: PropTypes.bool,
  onItemClick: PropTypes.func,
};

NotesListItem.defaultProps = {
  isCompressed: false,
  onItemClick: undefined,
};

export default React.memo(NotesListItem);
